import { useState, useRef, useEffect } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { useLocale } from "../lib/i18n";
import { supabase } from "../lib/supabase";

interface SearchResult {
  id: string;
  title: string;
  artist: string;
  title_he: string | null;
  artist_he: string | null;
  title_en: string | null;
  artist_en: string | null;
  artwork_url: string | null;
}

interface Props {
  onGuess: (artist: string, title: string) => void;
  disabled?: boolean;
  /** Increment from parent to signal a wrong guess — triggers shake, red flash, haptic. */
  wrongSignal?: number;
}

export function GuessInput({ onGuess, disabled, wrongSignal = 0 }: Props) {
  const { t, locale } = useLocale();
  const display = (raw: string, he: string | null, en: string | null) => {
    if (locale === "he" && he) return he;
    if (locale !== "he" && en) return en;
    return raw;
  };
  const [wrongFlash, setWrongFlash] = useState(false);

  useEffect(() => {
    if (!wrongSignal) return;
    navigator.vibrate?.(60);
    setWrongFlash(true);
    const timer = setTimeout(() => setWrongFlash(false), 500);
    return () => clearTimeout(timer);
  }, [wrongSignal]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const escaped = query.replace(/[%_,()]/g, "");
        const pattern = `%${escaped}%`;
        const { data } = await supabase
          .from("songs")
          .select("id, title, artist, title_he, artist_he, title_en, artist_en, artwork_url")
          .or(
            `title.ilike.${pattern},artist.ilike.${pattern},title_he.ilike.${pattern},artist_he.ilike.${pattern},title_en.ilike.${pattern},artist_en.ilike.${pattern}`
          )
          .not("itunes_id", "is", null)
          .limit(25);
        setResults(data ?? []);
        setShowResults(true);
        setHighlighted(-1);
      } catch { setResults([]); }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showResults) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showResults]);

  const handleSelect = (r: SearchResult) => {
    onGuess(r.artist, r.title);
    setQuery(`${display(r.artist, r.artist_he, r.artist_en)} — ${display(r.title, r.title_he, r.title_en)}`);
    setShowResults(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((prev) => (prev + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((prev) => (prev <= 0 ? results.length - 1 : prev - 1));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      handleSelect(results[highlighted]);
    } else if (e.key === "Escape") {
      setShowResults(false);
    }
  };

  return (
    <div className="w-full relative" ref={containerRef}>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setShowResults(true)}
        onKeyDown={handleKeyDown}
        placeholder={t("guess.placeholder")}
        disabled={disabled}
        className={`search-dark w-full py-4 px-5 rounded-2xl font-medium disabled:opacity-50 ${
          wrongFlash ? "animate-shake ring-2 ring-red-500/80" : ""
        }`}
      />
      <button
        className="absolute end-4 top-1/2 -translate-y-1/2"
        style={{ color: "var(--gold-500)" }}
      >
        <MagnifyingGlass size={20} weight="bold" />
      </button>

      {showResults && results.length > 0 && (
        <ul className="absolute z-30 mt-2 w-full rounded-2xl glass-solid overflow-y-auto overflow-x-hidden max-h-[60vh]">
          {results.map((r, i) => (
            <li key={r.id}>
              <button
                onClick={() => handleSelect(r)}
                className={`flex w-full items-center gap-3 px-4 py-3 text-start hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 ${i === highlighted ? "bg-white/10" : ""}`}
              >
                {r.artwork_url ? (
                  <img src={r.artwork_url} alt="" className="h-10 w-10 rounded-lg" />
                ) : (
                  <div className="h-10 w-10 rounded-lg bg-gray-700" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-white">{display(r.title, r.title_he, r.title_en)}</div>
                  <div className="truncate text-xs text-zinc-400">{display(r.artist, r.artist_he, r.artist_en)}</div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
